import { CommonModule } from '@angular/common';
import DailyIframe, { type DailyCall, type DailyCallOptions, type DailyWaitingParticipant, type DailyEventObjectWaitingParticipant } from '@daily-co/daily-js';
import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';

import { selectUser } from '../../../store/user/user.selectors';
import { CalendarInviteParticipantRequest, CalendarInviteParticipantSuccess } from '../../../store/calendar/calendar.actions';
import { Actions, ofType } from '@ngrx/effects';
import { SnackbarService } from '../../../core/services';
import { take } from 'rxjs';
import { BRANDING, withBrandName } from '../../../core/config/branding';

type CallSessionData = {
  id: string;
  channelId: string | null;
  calendarEventId?: string | null;
  launchUrl: string;
  title: string;
  mode: 'audio' | 'video';
  createdAt: string;
  userName?: string;
};

const CALL_SESSION_STORAGE_PREFIX = 'meetrix.daily.session.';
const AUDIO_INPUT_STORAGE_KEY = 'meetrix.daily.audioInputId';
const AUDIO_OUTPUT_STORAGE_KEY = 'meetrix.daily.audioOutputId';
const VIDEO_INPUT_STORAGE_KEY = 'meetrix.daily.videoInputId';

type CallSessionStatus = 'loading' | 'connecting' | 'joined' | 'leaving' | 'error';

@Component({
  selector: 'app-call-session',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule],
  templateUrl: './call-session.html',
  styleUrl: './call-session.scss',
})
export class CallSession {
  protected readonly brand = BRANDING;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly frameHost = viewChild<ElementRef<HTMLElement>>('frameHost');
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly actions$ = inject(Actions);
  protected readonly snackbar = inject(SnackbarService);
  private callFrame: DailyCall | null = null;
  private initialized = false;
  private leaving = false;

  protected readonly session = signal<CallSessionData | null>(null);
  private readonly currentUserName = signal<string>('');
  protected readonly status = signal<CallSessionStatus>('loading');
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly waitingParticipants = signal<DailyWaitingParticipant[]>([]);
  private admitting = new Set<string>();

  ngOnInit(): void {
    const sessionId = this.route.snapshot.paramMap.get('sessionId');
    if (!sessionId || typeof window === 'undefined') {
      void this.router.navigate(['/home/chat']);
      return;
    }

    const stored = window.localStorage.getItem(CALL_SESSION_STORAGE_PREFIX + sessionId);
    if (!stored) {
      void this.router.navigate(['/home/chat']);
      return;
    }

    try {
      const parsed = JSON.parse(stored) as CallSessionData;
      this.session.set(parsed);
      this.status.set('connecting');
      this.store
        .select(selectUser)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((user) => this.currentUserName.set(user?.name?.trim() ?? ''));
      queueMicrotask(() => this.maybeInitializeCall());
    } catch {
      void this.router.navigate(['/home/chat']);
    }
  }

  ngAfterViewInit(): void {
    this.maybeInitializeCall();
  }

  ngOnDestroy(): void {
    void this.teardownCall({ preserveSession: true });
  }

  protected async leave(): Promise<void> {
    this.leaving = true;
    this.status.set('leaving');
    await this.teardownCall({ preserveSession: false });

    try {
      window.close();
    } catch {}

    if (typeof window !== 'undefined' && !window.closed) {
      void this.router.navigate(['/home/chat']);
    }
  }

  protected openInviteDialog(): void {
    const eventId = this.session()?.calendarEventId?.trim?.() ?? '';
    if (!eventId) {
      return;
    }

    const ref = this.dialog.open(InviteParticipantDialog, {
      width: '520px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: { eventId },
    });

    ref.afterClosed().subscribe((result) => {
      const payload = result?.payload as { email?: string; name?: string } | undefined;
      if (!payload?.email) {
        return;
      }

      this.store.dispatch(CalendarInviteParticipantRequest({ eventId, email: payload.email, name: payload.name }));

      this.actions$.pipe(ofType(CalendarInviteParticipantSuccess), take(1)).subscribe(() => {
        this.snackbar.showNotification('success', '', 'Participant invited successfully.', 'Close');
      });
    });
  }

  protected readonly callAriaLabel = withBrandName('call session');
  protected readonly callEyebrow = withBrandName('Call');
  protected readonly dailyCallAriaLabel = withBrandName('Daily call');

  private maybeInitializeCall(): void {
    if (this.initialized) {
      return;
    }

    const session = this.session();
    const host = this.frameHost()?.nativeElement;
    if (!session || !host || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;
    void this.initializeCall(session, host);
  }

  private async initializeCall(session: CallSessionData, host: HTMLElement): Promise<void> {
    try {
      const parsedLaunch = this.parseLaunchUrl(session.launchUrl);
      const joinOptions: DailyCallOptions = {
        url: parsedLaunch.roomUrl,
        userName: session.userName?.trim?.() || this.currentUserName() || undefined,
        startVideoOff: session.mode === 'audio',
        startAudioOff: false,
      };

      if (typeof parsedLaunch.token === 'string' && parsedLaunch.token.trim()) {
        joinOptions.token = parsedLaunch.token;
      }

      const preferredAudioInput = this.getStoredPreference(AUDIO_INPUT_STORAGE_KEY);
      const preferredVideoInput = this.getStoredPreference(VIDEO_INPUT_STORAGE_KEY);
      const preferredAudioOutput = this.getStoredPreference(AUDIO_OUTPUT_STORAGE_KEY);

      if (preferredAudioInput) {
        joinOptions.audioSource = preferredAudioInput;
      }

      if (session.mode === 'video') {
        joinOptions.videoSource = preferredVideoInput || true;
      } else {
        joinOptions.videoSource = false;
      }

      const callFrame = DailyIframe.createFrame(host, {
        showLeaveButton: false,
        showFullscreenButton: false,
        showParticipantsBar: true,
        showUserNameChangeUI: false,
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '20px',
          backgroundColor: '#020617',
        },
      });

      this.callFrame = callFrame;

      callFrame.on('joined-meeting', async () => {
        this.status.set('joined');
        this.refreshWaitingParticipants();

        if (preferredAudioOutput) {
          try {
            await callFrame.setOutputDeviceAsync({ outputDeviceId: preferredAudioOutput });
          } catch {}
        }
      });

      callFrame.on('left-meeting', () => {
        if (this.leaving) {
          return;
        }

        this.status.set('leaving');
        void this.leave();
      });

      callFrame.on('error', (event) => {
        const message = typeof event?.errorMsg === 'string' ? event.errorMsg : 'We could not connect this call.';
        this.handleCallError(message);
      });

      callFrame.on('camera-error', (event) => {
        const message = typeof event?.errorMsg === 'string' ? event.errorMsg : 'Camera or microphone access failed.';
        this.handleCallError(message);
      });

      callFrame.on('waiting-participant-added', (event: DailyEventObjectWaitingParticipant) => {
        this.upsertWaitingParticipant(event?.participant);
      });
      callFrame.on('waiting-participant-updated', (event: DailyEventObjectWaitingParticipant) => {
        this.upsertWaitingParticipant(event?.participant);
      });
      callFrame.on('waiting-participant-removed', (event: DailyEventObjectWaitingParticipant) => {
        const id = event?.participant?.id;
        if (!id) {
          return;
        }
        this.waitingParticipants.set(this.waitingParticipants().filter((p) => p.id !== id));
      });

      await callFrame.join(joinOptions);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'We could not start this Daily call.';
      this.handleCallError(message);
    }
  }

  private parseLaunchUrl(launchUrl: string): { roomUrl: string; token: string | null } {
    const parsed = new URL(launchUrl);
    const token = parsed.searchParams.get('t');
    parsed.searchParams.delete('t');

    return {
      roomUrl: parsed.toString(),
      token,
    };
  }

  private getStoredPreference(storageKey: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const value = window.localStorage.getItem(storageKey)?.trim() ?? '';
    return value || null;
  }

  private handleCallError(message: string): void {
    this.status.set('error');
    this.errorMessage.set(message);
    void this.teardownCall({ preserveSession: true });
  }

  protected async admitWaitingParticipant(id: string): Promise<void> {
    const callFrame = this.callFrame;
    if (!callFrame || !id || this.admitting.has(id)) {
      return;
    }

    this.admitting.add(id);
    try {
      await callFrame.updateWaitingParticipant(id, { grantRequestedAccess: true });
      this.waitingParticipants.set(this.waitingParticipants().filter((p) => p.id !== id));
    } catch {
      // noop
    } finally {
      this.admitting.delete(id);
    }
  }

  private refreshWaitingParticipants(): void {
    const callFrame = this.callFrame;
    if (!callFrame) {
      return;
    }
    try {
      const map = callFrame.waitingParticipants() ?? {};
      this.waitingParticipants.set(Object.values(map));
    } catch {}
  }

  private upsertWaitingParticipant(participant: DailyWaitingParticipant | undefined): void {
    if (!participant?.id) {
      return;
    }
    const existing = this.waitingParticipants();
    const next = existing.some((p) => p.id === participant.id)
      ? existing.map((p) => (p.id === participant.id ? participant : p))
      : [...existing, participant];
    this.waitingParticipants.set(next);
  }

  private async teardownCall(options: { preserveSession: boolean }): Promise<void> {
    const session = this.session();

    if (!options.preserveSession && session && typeof window !== 'undefined') {
      window.localStorage.removeItem(CALL_SESSION_STORAGE_PREFIX + session.id);
    }

    const callFrame = this.callFrame;
    this.callFrame = null;

    if (!callFrame) {
      return;
    }

    try {
      if (callFrame.meetingState() === 'joined-meeting' || callFrame.meetingState() === 'joining-meeting') {
        await callFrame.leave();
      }
    } catch {}

    try {
      await callFrame.destroy();
    } catch {}
  }
}

type InviteParticipantDialogData = {
  eventId: string;
};

@Component({
  selector: 'app-invite-participant-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule],
  template: `
    <header class="invite-dialog__header">
      <h2 mat-dialog-title>Add a person</h2>
    </header>

    <mat-dialog-content class="invite-dialog__content">
      <p class="invite-dialog__hint">
        Enter an email to invite. If it’s an external participant, also provide a name.
      </p>

      <form [formGroup]="form" class="invite-dialog__form" (ngSubmit)="submit()">
        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <input matInput formControlName="email" autocomplete="email" />
          @if (form.controls.email.hasError('required')) {
            <mat-error>Email is required</mat-error>
          }
          @if (form.controls.email.hasError('email')) {
            <mat-error>Enter a valid email</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Name (external only)</mat-label>
          <input matInput formControlName="name" autocomplete="name" />
        </mat-form-field>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" (click)="submit()">Send invite</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .invite-dialog__header {
        padding: 6px 24px 0;
      }

      .invite-dialog__content {
        padding-top: 6px;
      }

      .invite-dialog__hint {
        margin: 0 0 14px;
        color: rgba(148, 163, 184, 0.95);
        font-size: 0.95rem;
      }

      .invite-dialog__form {
        display: grid;
        gap: 14px;
        min-width: min(520px, 100%);
      }
    `,
  ],
})
class InviteParticipantDialog {
  private readonly dialogRef = inject(MatDialogRef<InviteParticipantDialog>);
  protected readonly data = inject<InviteParticipantDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  protected readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    name: [''],
  });

  protected close(): void {
    this.dialogRef.close();
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const email = this.form.controls.email.value?.trim() ?? '';
    const name = this.form.controls.name.value?.trim() ?? '';
    this.dialogRef.close({ payload: { email, name: name || undefined } });
  }
}
